import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Breadcrumb from '../components/Breadcrumb';
import { 
  FileText, Clock, CheckCircle, XCircle, Play, 
  Trophy, TrendingUp, Calendar, BarChart3 
} from 'lucide-react';

const MyQuizzes = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [activeTab, setActiveTab] = useState('available');
  const [availableQuizzes, setAvailableQuizzes] = useState([]);
  const [myAttempts, setMyAttempts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, passed: 0, averageScore: 0 });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Load my attempts
      const attemptsRes = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/assessments/my-attempts`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (attemptsRes.ok) {
        const attemptsData = await attemptsRes.json();
        setMyAttempts(attemptsData);
        
        // Calculate stats
        const completed = attemptsData.filter(a => a.completedAt);
        const passed = completed.filter(a => a.passed);
        const avgScore = completed.length > 0 
          ? Math.round(completed.reduce((sum, a) => sum + (a.percentage || 0), 0) / completed.length)
          : 0;
        
        setStats({
          total: completed.length,
          passed: passed.length,
          averageScore: avgScore
        });
      }

      // Load available quizzes from enrolled courses
      const enrollmentsRes = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/courses/my-enrollments/detailed`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (enrollmentsRes.ok) {
        const enrollments = await enrollmentsRes.json();
        
        // Collect all quizzes from enrolled courses
        const quizzes = [];
        for (const enrollment of enrollments) {
          if (enrollment.course?.id) {
            // Get lessons for this course
            const lessonsRes = await fetch(
              `${process.env.REACT_APP_BACKEND_URL}/api/yomev/courses/${enrollment.course.id}/sections`,
              { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (lessonsRes.ok) {
              const sections = await lessonsRes.json();
              for (const section of sections) {
                for (const lesson of section.lessons || []) {
                  // Get quizzes for this lesson
                  const quizzesRes = await fetch(
                    `${process.env.REACT_APP_BACKEND_URL}/api/assessments/lessons/${lesson.id}/quizzes`,
                    { headers: { 'Authorization': `Bearer ${token}` } }
                  );
                  if (quizzesRes.ok) {
                    const lessonQuizzes = await quizzesRes.json();
                    for (const quiz of lessonQuizzes) {
                      if (quiz.isPublished) {
                        quizzes.push({
                          ...quiz,
                          courseName: enrollment.course.title,
                          lessonName: lesson.title
                        });
                      }
                    }
                  }
                }
              }
            }
          }
        }
        setAvailableQuizzes(quizzes);
      }
    } catch (error) {
      console.error('Veri yüklenemedi:', error);
    } finally {
      setLoading(false);
    }
  };

  const getAttemptForQuiz = (quizId) => {
    return myAttempts.find(a => a.quizId === quizId && a.completedAt);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div>
      <Breadcrumb items={[{ label: 'Quiz & Testlerim' }]} />
      
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">📝 Quiz & Testlerim</h1>
        <p className="text-gray-600">Katıldığınız kurslardan quiz'leri çözün ve sonuçlarınızı takip edin</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-xl shadow-sm p-4 flex items-center gap-4">
          <div className="p-3 bg-blue-100 rounded-lg">
            <FileText className="text-blue-600" size={24} />
          </div>
          <div>
            <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            <p className="text-sm text-gray-500">Tamamlanan Quiz</p>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-4 flex items-center gap-4">
          <div className="p-3 bg-green-100 rounded-lg">
            <Trophy className="text-green-600" size={24} />
          </div>
          <div>
            <p className="text-2xl font-bold text-green-600">{stats.passed}</p>
            <p className="text-sm text-gray-500">Başarılı</p>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-4 flex items-center gap-4">
          <div className="p-3 bg-purple-100 rounded-lg">
            <TrendingUp className="text-purple-600" size={24} />
          </div>
          <div>
            <p className="text-2xl font-bold text-purple-600">%{stats.averageScore}</p>
            <p className="text-sm text-gray-500">Ortalama Başarı</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-sm mb-6">
        <div className="border-b">
          <div className="flex">
            <button
              onClick={() => setActiveTab('available')}
              className={`px-6 py-4 font-medium border-b-2 ${
                activeTab === 'available'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              📋 Mevcut Quiz'ler ({availableQuizzes.length})
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={`px-6 py-4 font-medium border-b-2 ${
                activeTab === 'history'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              📊 Geçmişim ({myAttempts.filter(a => a.completedAt).length})
            </button>
          </div>
        </div>

        <div className="p-4">
          {activeTab === 'available' && (
            <>
              {availableQuizzes.length === 0 ? (
                <div className="text-center py-12">
                  <FileText size={48} className="mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500">Henüz çözebileceğiniz quiz bulunmuyor</p>
                  <p className="text-sm text-gray-400 mt-1">Kurslara kaydolduğunuzda quiz'ler burada görünecek</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {availableQuizzes.map(quiz => {
                    const attempt = getAttemptForQuiz(quiz.id);
                    return (
                      <div 
                        key={quiz.id} 
                        className="flex items-center justify-between p-4 border rounded-lg hover:border-blue-300 transition-colors"
                      >
                        <div className="flex items-center gap-4">
                          <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                            attempt?.passed ? 'bg-green-100' : 
                            attempt ? 'bg-red-100' : 'bg-blue-100'
                          }`}>
                            {attempt?.passed ? (
                              <CheckCircle className="text-green-600" size={24} />
                            ) : attempt ? (
                              <XCircle className="text-red-600" size={24} />
                            ) : (
                              <FileText className="text-blue-600" size={24} />
                            )}
                          </div>
                          <div>
                            <h3 className="font-semibold text-gray-900">{quiz.title}</h3>
                            <p className="text-sm text-gray-500">
                              {quiz.courseName} • {quiz.lessonName}
                            </p>
                            <div className="flex items-center gap-3 mt-1 text-xs text-gray-400">
                              <span>{quiz.questionCount} soru</span>
                              <span>•</span>
                              <span>{quiz.totalPoints} puan</span>
                              {quiz.duration && (
                                <>
                                  <span>•</span>
                                  <span className="flex items-center gap-1">
                                    <Clock size={12} />
                                    {quiz.duration} dk
                                  </span>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          {attempt && (
                            <div className="text-right mr-4">
                              <p className={`text-lg font-bold ${attempt.passed ? 'text-green-600' : 'text-red-600'}`}>
                                %{attempt.percentage}
                              </p>
                              <p className="text-xs text-gray-500">Son sonuç</p>
                            </div>
                          )}
                          <button
                            onClick={() => navigate(`/quiz/${quiz.id}`)}
                            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                          >
                            <Play size={16} />
                            {attempt ? 'Tekrar Çöz' : 'Başla'}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}

          {activeTab === 'history' && (
            <>
              {myAttempts.filter(a => a.completedAt).length === 0 ? (
                <div className="text-center py-12">
                  <BarChart3 size={48} className="mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500">Henüz quiz çözmediniz</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {myAttempts
                    .filter(a => a.completedAt)
                    .sort((a, b) => new Date(b.completedAt) - new Date(a.completedAt))
                    .map(attempt => (
                      <div 
                        key={attempt.id} 
                        className="flex items-center justify-between p-4 border rounded-lg"
                      >
                        <div className="flex items-center gap-4">
                          <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                            attempt.passed ? 'bg-green-100' : 'bg-red-100'
                          }`}>
                            {attempt.passed ? (
                              <CheckCircle className="text-green-600" size={24} />
                            ) : (
                              <XCircle className="text-red-600" size={24} />
                            )}
                          </div>
                          <div>
                            <h3 className="font-semibold text-gray-900">
                              {attempt.quiz?.title || 'Quiz'}
                            </h3>
                            <div className="flex items-center gap-2 text-sm text-gray-500">
                              <Calendar size={14} />
                              {new Date(attempt.completedAt).toLocaleDateString('tr-TR', {
                                day: 'numeric',
                                month: 'long',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-6">
                          <div className="text-center">
                            <p className={`text-2xl font-bold ${attempt.passed ? 'text-green-600' : 'text-red-600'}`}>
                              %{attempt.percentage}
                            </p>
                            <p className="text-xs text-gray-500">{attempt.score}/{attempt.totalPoints} puan</p>
                          </div>
                          <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                            attempt.passed 
                              ? 'bg-green-100 text-green-700' 
                              : 'bg-red-100 text-red-700'
                          }`}>
                            {attempt.passed ? '✓ Başarılı' : '✗ Başarısız'}
                          </div>
                        </div>
                      </div>
                    ))
                  }
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default MyQuizzes;
